export type SourceObjectId =
  | 'loan'
  | 'call'
  | 'disclosure'
  | 'submission'
  | 'partner_activity'
  | 'account'
  | 'contact'
  | 'opportunity'
  | 'lead'
  | 'case'
  | 'task'
  | 'event'
  | 'campaign'
  | 'quote'
  | 'contract'

export type FilterScope = 'source' | 'related'

export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'does_not_contain'
  | 'greater_than'
  | 'less_than'
  | 'is_true'
  | 'is_false'

export interface QueryField {
  key: string
  label: string
  valueType: 'text' | 'number' | 'boolean' | 'date'
}

export interface RelatedRecordSchema {
  key: string
  label: string
  fields: QueryField[]
}

export interface SourceObjectSchema {
  id: SourceObjectId
  label: string
  description: string
  userFields: QueryField[]
  sourceFields: QueryField[]
  relatedFields: QueryField[]
}

export const SOURCE_OBJECT_SCHEMAS: SourceObjectSchema[] = [
  {
    id: 'loan',
    label: 'Loan File',
    description: 'Mortgage loan records and their lifecycle events.',
    userFields: [
      { key: 'loanOfficer', label: 'Loan Officer', valueType: 'text' },
      { key: 'assignedOfficer', label: 'Assigned Officer', valueType: 'text' },
      { key: 'createdBy', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'stage', label: 'Current Stage', valueType: 'text' },
      { key: 'loanType', label: 'Loan Type', valueType: 'text' },
      { key: 'loanAmount', label: 'Loan Amount', valueType: 'number' },
      { key: 'daysInStage', label: 'Days in Stage', valueType: 'number' },
      { key: 'isMoving', label: 'Is Moving', valueType: 'boolean' },
      { key: 'closeDate', label: 'Close Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'borrower.state', label: 'Borrower · State', valueType: 'text' },
      { key: 'borrower.creditScore', label: 'Borrower · Credit Score', valueType: 'number' },
      { key: 'lender.name', label: 'Lender · Name', valueType: 'text' },
      { key: 'property.county', label: 'Property · County', valueType: 'text' },
    ],
  },
  {
    id: 'call',
    label: 'Dialpad Call',
    description: 'Inbound and outbound activity from the calling platform.',
    userFields: [
      { key: 'owner', label: 'Call Owner', valueType: 'text' },
      { key: 'assignedOfficer', label: 'Assigned Officer', valueType: 'text' },
      { key: 'createdBy', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'direction', label: 'Direction', valueType: 'text' },
      { key: 'answered', label: 'Was Answered', valueType: 'boolean' },
      { key: 'durationSeconds', label: 'Duration (seconds)', valueType: 'number' },
      { key: 'outcome', label: 'Outcome', valueType: 'text' },
      { key: 'callDate', label: 'Call Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'contact.type', label: 'Contact · Type', valueType: 'text' },
      { key: 'lead.source', label: 'Lead · Source', valueType: 'text' },
      { key: 'campaign.name', label: 'Campaign · Name', valueType: 'text' },
    ],
  },
  {
    id: 'disclosure',
    label: 'Disclosure Package',
    description: 'Initial disclosure and borrower signing records.',
    userFields: [
      { key: 'loanOfficer', label: 'Loan Officer', valueType: 'text' },
      { key: 'sentBy', label: 'Sent By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'status', label: 'Disclosure Status', valueType: 'text' },
      { key: 'sentAt', label: 'Sent At', valueType: 'date' },
      { key: 'signedAt', label: 'Signed At', valueType: 'date' },
      { key: 'hoursToSign', label: 'Hours to Sign', valueType: 'number' },
    ],
    relatedFields: [
      { key: 'loan.loanType', label: 'Loan · Loan Type', valueType: 'text' },
      { key: 'borrower.state', label: 'Borrower · State', valueType: 'text' },
    ],
  },
  {
    id: 'submission',
    label: 'Underwriting Submission',
    description: 'Lender submissions and condition outcomes.',
    userFields: [
      { key: 'loanOfficer', label: 'Loan Officer', valueType: 'text' },
      { key: 'submittedBy', label: 'Submitted By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'lender', label: 'Lender', valueType: 'text' },
      { key: 'conditionCount', label: 'Condition Count', valueType: 'number' },
      { key: 'submittedAt', label: 'Submitted At', valueType: 'date' },
      { key: 'status', label: 'Submission Status', valueType: 'text' },
    ],
    relatedFields: [
      { key: 'loan.loanAmount', label: 'Loan · Loan Amount', valueType: 'number' },
      { key: 'borrower.creditScore', label: 'Borrower · Credit Score', valueType: 'number' },
    ],
  },
  {
    id: 'partner_activity',
    label: 'Loan Partner Activity',
    description: 'SAFE study and licensing completion activity.',
    userFields: [
      { key: 'partner', label: 'Loan Partner', valueType: 'text' },
      { key: 'submittedBy', label: 'Submitted By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'activityType', label: 'Activity Type', valueType: 'text' },
      { key: 'completedAt', label: 'Completed At', valueType: 'date' },
      { key: 'attempt', label: 'Attempt', valueType: 'number' },
    ],
    relatedFields: [
      { key: 'partner.status', label: 'Partner · Status', valueType: 'text' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    description: 'Standard Salesforce Account records.',
    userFields: [
      { key: 'ownerId', label: 'Account Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'name', label: 'Account Name', valueType: 'text' },
      { key: 'type', label: 'Type', valueType: 'text' },
      { key: 'industry', label: 'Industry', valueType: 'text' },
      { key: 'annualRevenue', label: 'Annual Revenue', valueType: 'number' },
      { key: 'rating', label: 'Rating', valueType: 'text' },
      { key: 'createdDate', label: 'Created Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'parent.name', label: 'Parent Account · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
      { key: 'contacts.email', label: 'Contact · Email', valueType: 'text' },
    ],
  },
  {
    id: 'contact',
    label: 'Contact',
    description: 'Standard Salesforce Contact records.',
    userFields: [
      { key: 'ownerId', label: 'Contact Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'firstName', label: 'First Name', valueType: 'text' },
      { key: 'lastName', label: 'Last Name', valueType: 'text' },
      { key: 'email', label: 'Email', valueType: 'text' },
      { key: 'title', label: 'Title', valueType: 'text' },
      { key: 'leadSource', label: 'Lead Source', valueType: 'text' },
      { key: 'createdDate', label: 'Created Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'account.name', label: 'Account · Name', valueType: 'text' },
      { key: 'account.industry', label: 'Account · Industry', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
    ],
  },
  {
    id: 'opportunity',
    label: 'Opportunity',
    description: 'Standard Salesforce sales opportunities.',
    userFields: [
      { key: 'ownerId', label: 'Opportunity Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'name', label: 'Opportunity Name', valueType: 'text' },
      { key: 'stageName', label: 'Stage', valueType: 'text' },
      { key: 'amount', label: 'Amount', valueType: 'number' },
      { key: 'probability', label: 'Probability', valueType: 'number' },
      { key: 'closeDate', label: 'Close Date', valueType: 'date' },
      { key: 'type', label: 'Type', valueType: 'text' },
    ],
    relatedFields: [
      { key: 'account.name', label: 'Account · Name', valueType: 'text' },
      { key: 'account.industry', label: 'Account · Industry', valueType: 'text' },
      { key: 'campaign.name', label: 'Campaign · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
    ],
  },
  {
    id: 'lead',
    label: 'Lead',
    description: 'Standard Salesforce lead records.',
    userFields: [
      { key: 'ownerId', label: 'Lead Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'company', label: 'Company', valueType: 'text' },
      { key: 'status', label: 'Status', valueType: 'text' },
      { key: 'leadSource', label: 'Lead Source', valueType: 'text' },
      { key: 'rating', label: 'Rating', valueType: 'text' },
      { key: 'annualRevenue', label: 'Annual Revenue', valueType: 'number' },
      { key: 'createdDate', label: 'Created Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'campaign.name', label: 'Campaign · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
      { key: 'convertedAccount.name', label: 'Converted Account · Name', valueType: 'text' },
    ],
  },
  {
    id: 'case',
    label: 'Case',
    description: 'Standard Salesforce service case records.',
    userFields: [
      { key: 'ownerId', label: 'Case Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'caseNumber', label: 'Case Number', valueType: 'text' },
      { key: 'status', label: 'Status', valueType: 'text' },
      { key: 'priority', label: 'Priority', valueType: 'text' },
      { key: 'origin', label: 'Origin', valueType: 'text' },
      { key: 'isClosed', label: 'Is Closed', valueType: 'boolean' },
      { key: 'createdDate', label: 'Created Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'account.name', label: 'Account · Name', valueType: 'text' },
      { key: 'contact.email', label: 'Contact · Email', valueType: 'text' },
      { key: 'parent.caseNumber', label: 'Parent Case · Case Number', valueType: 'text' },
    ],
  },
  {
    id: 'task',
    label: 'Task',
    description: 'Standard Salesforce activity tasks.',
    userFields: [
      { key: 'ownerId', label: 'Task Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'subject', label: 'Subject', valueType: 'text' },
      { key: 'status', label: 'Status', valueType: 'text' },
      { key: 'priority', label: 'Priority', valueType: 'text' },
      { key: 'activityDate', label: 'Due Date', valueType: 'date' },
      { key: 'isClosed', label: 'Is Closed', valueType: 'boolean' },
      { key: 'createdDate', label: 'Created Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'who.name', label: 'Name · Full Name', valueType: 'text' },
      { key: 'what.name', label: 'Related To · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
    ],
  },
  {
    id: 'event',
    label: 'Event',
    description: 'Standard Salesforce calendar events.',
    userFields: [
      { key: 'ownerId', label: 'Event Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'subject', label: 'Subject', valueType: 'text' },
      { key: 'startDateTime', label: 'Start Date & Time', valueType: 'date' },
      { key: 'endDateTime', label: 'End Date & Time', valueType: 'date' },
      { key: 'isAllDayEvent', label: 'Is All Day', valueType: 'boolean' },
      { key: 'type', label: 'Type', valueType: 'text' },
      { key: 'createdDate', label: 'Created Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'who.name', label: 'Name · Full Name', valueType: 'text' },
      { key: 'what.name', label: 'Related To · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
    ],
  },
  {
    id: 'campaign',
    label: 'Campaign',
    description: 'Standard Salesforce marketing campaigns.',
    userFields: [
      { key: 'ownerId', label: 'Campaign Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'name', label: 'Campaign Name', valueType: 'text' },
      { key: 'status', label: 'Status', valueType: 'text' },
      { key: 'type', label: 'Type', valueType: 'text' },
      { key: 'budgetedCost', label: 'Budgeted Cost', valueType: 'number' },
      { key: 'startDate', label: 'Start Date', valueType: 'date' },
      { key: 'endDate', label: 'End Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'parent.name', label: 'Parent Campaign · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
      { key: 'members.status', label: 'Member · Status', valueType: 'text' },
    ],
  },
  {
    id: 'quote',
    label: 'Quote',
    description: 'Standard Salesforce quote records.',
    userFields: [
      { key: 'ownerId', label: 'Quote Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'name', label: 'Quote Name', valueType: 'text' },
      { key: 'status', label: 'Status', valueType: 'text' },
      { key: 'grandTotal', label: 'Grand Total', valueType: 'number' },
      { key: 'expirationDate', label: 'Expiration Date', valueType: 'date' },
      { key: 'isSyncing', label: 'Is Syncing', valueType: 'boolean' },
    ],
    relatedFields: [
      { key: 'opportunity.name', label: 'Opportunity · Name', valueType: 'text' },
      { key: 'account.name', label: 'Account · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
    ],
  },
  {
    id: 'contract',
    label: 'Contract',
    description: 'Standard Salesforce contract records.',
    userFields: [
      { key: 'ownerId', label: 'Contract Owner', valueType: 'text' },
      { key: 'createdById', label: 'Created By', valueType: 'text' },
    ],
    sourceFields: [
      { key: 'contractNumber', label: 'Contract Number', valueType: 'text' },
      { key: 'status', label: 'Status', valueType: 'text' },
      { key: 'startDate', label: 'Start Date', valueType: 'date' },
      { key: 'endDate', label: 'End Date', valueType: 'date' },
      { key: 'contractTerm', label: 'Contract Term', valueType: 'number' },
      { key: 'activatedDate', label: 'Activated Date', valueType: 'date' },
    ],
    relatedFields: [
      { key: 'account.name', label: 'Account · Name', valueType: 'text' },
      { key: 'owner.role', label: 'Owner · Role', valueType: 'text' },
    ],
  },
]

const LEGACY_SOURCE_OBJECT_IDS: SourceObjectId[] = [
  'loan',
  'call',
  'disclosure',
  'submission',
  'partner_activity',
]

export const SELECTABLE_SOURCE_OBJECT_SCHEMAS = SOURCE_OBJECT_SCHEMAS.filter(
  (schema) => !LEGACY_SOURCE_OBJECT_IDS.includes(schema.id),
)

export function isSelectableSourceObject(
  id: string,
): id is SourceObjectId {
  return SELECTABLE_SOURCE_OBJECT_SCHEMAS.some((schema) => schema.id === id)
}

export function getRelatedRecordSchemas(
  schema: SourceObjectSchema,
): RelatedRecordSchema[] {
  const records = new Map<string, RelatedRecordSchema>()

  schema.relatedFields.forEach((field) => {
    const [recordKey, ...fieldKeyParts] = field.key.split('.')
    if (!recordKey || fieldKeyParts.length === 0) return

    const [recordLabel, ...fieldLabelParts] = field.label.split(' · ')
    const record = records.get(recordKey) ?? {
      key: recordKey,
      label: recordLabel || recordKey,
      fields: [],
    }

    record.fields.push({
      ...field,
      key: fieldKeyParts.join('.'),
      label: fieldLabelParts.join(' · ') || field.label,
    })
    records.set(recordKey, record)
  })

  return Array.from(records.values())
}

export const FILTER_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'is_true', label: 'Is true' },
  { value: 'is_false', label: 'Is false' },
]

const NEGATED_OPERATORS: Record<FilterOperator, FilterOperator> = {
  equals: 'not_equals',
  not_equals: 'equals',
  contains: 'does_not_contain',
  does_not_contain: 'contains',
  greater_than: 'less_than',
  less_than: 'greater_than',
  is_true: 'is_false',
  is_false: 'is_true',
}

export function negateOperator(operator: FilterOperator): FilterOperator {
  return NEGATED_OPERATORS[operator] ?? operator
}

export function getSourceObjectSchema(id: SourceObjectId): SourceObjectSchema {
  return (
    SOURCE_OBJECT_SCHEMAS.find((schema) => schema.id === id) ??
    SOURCE_OBJECT_SCHEMAS[0]
  )
}
